import { ErrorHandler, Injectable } from '@angular/core';
import { faro } from '@grafana/faro-web-sdk';

/**
 * Custom Angular ErrorHandler that integrates with Grafana Faro.
 * Captures all unhandled errors and sends them to Faro for monitoring.
 * Includes deduplication to prevent the same error from flooding logs.
 *
 * Based on: https://github.com/grafana/faro-web-sdk/blob/main/docs/sources/tutorials/use-angular.md
 */
@Injectable()
export class FaroErrorHandler implements ErrorHandler {
  private recentErrors = new Map<string, number>();
  private readonly DEDUPE_WINDOW_MS = 10000; // 10 seconds deduplication window
  private readonly MAX_SAME_ERROR = 1; // Only report same error once in window

  handleError(error: unknown): void {
    // Extract the original error if wrapped
    const originalError = this.extractError(error);

    // Create a unique key for this error
    const errorKey = this.getErrorKey(originalError);

    // Check for duplicate errors
    if (this.isDuplicateError(errorKey)) {
      // Still log to console but don't send to Faro
      if (this.shouldLogToConsole(errorKey)) {
        console.error(
          'Application Error (suppressed from Faro):',
          originalError,
        );
      }
      return;
    }

    // Send to Faro (only if Faro is initialized)
    if (originalError instanceof Error && faro.api) {
      faro.api.pushError(originalError);
    }

    // Also log to console for development
    console.error('Application Error:', originalError);
  }

  /**
   * Check if we should still log to console (limit console spam too)
   */
  private shouldLogToConsole(errorKey: string): boolean {
    const lastReport = this.recentErrors.get(errorKey);
    if (!lastReport) return true;

    const count = Math.floor(lastReport / 1e12);
    return count <= 5; // Only log first 5 occurrences to console
  }

  /**
   * Generate a unique key for an error based on message and stack
   */
  private getErrorKey(error: Error): string {
    const message = error.message || '';
    const stack = error.stack?.split('\n').slice(0, 3).join('') || '';
    return `${message}:${stack}`;
  }

  /**
   * Check if this error was recently reported (deduplication)
   */
  private isDuplicateError(errorKey: string): boolean {
    const now = Date.now();
    const lastReport = this.recentErrors.get(errorKey);

    // Clean up old entries
    this.cleanupOldErrors(now);

    if (lastReport) {
      const count = Math.floor(lastReport / 1e12); // Extract count from encoded value
      const timestamp = lastReport % 1e12; // Extract timestamp

      if (now - timestamp < this.DEDUPE_WINDOW_MS) {
        if (count >= this.MAX_SAME_ERROR) {
          return true; // Skip this duplicate
        }
        // Increment count
        this.recentErrors.set(errorKey, (count + 1) * 1e12 + timestamp);
        return false;
      }
    }

    // First occurrence or outside window
    this.recentErrors.set(errorKey, 1 * 1e12 + now);
    return false;
  }

  /**
   * Remove errors older than the deduplication window
   */
  private cleanupOldErrors(now: number): void {
    for (const [key, value] of this.recentErrors.entries()) {
      const timestamp = value % 1e12;
      if (now - timestamp > this.DEDUPE_WINDOW_MS) {
        this.recentErrors.delete(key);
      }
    }
  }

  /**
   * Extract the original error from wrapped errors (e.g., from Zone.js or Angular)
   */
  private extractError(error: unknown): Error {
    // Handle Angular wrapped errors
    if (error && typeof error === 'object' && 'rejection' in error) {
      return this.extractError((error as { rejection: unknown }).rejection);
    }

    // Handle errors with originalError property
    if (error && typeof error === 'object' && 'originalError' in error) {
      return this.extractError(
        (error as { originalError: unknown }).originalError,
      );
    }

    // Handle Error instances
    if (error instanceof Error) {
      return error;
    }

    // Convert other types to Error
    return new Error(String(error));
  }
}
